import { initials } from "../misc/initials";
import {
  AvatarFallback,
  AvatarImage,
  Avatar as ShadcnAvatar,
} from "@/components/ui/avatar";
import { useRecordContext } from "ra-core";

import type { Contact } from "../types";

// Sprint 7 T7.2.b — Palette pastel pour les avatars sans image.
// Cf maquette design/atomic-crm/contacts.css (.avatar.peach/mint/sky/
// lav/pink/cream). Couleur déterministe à partir des initiales pour
// que Marc Dubois ait toujours la même couleur.
const PASTEL_PALETTE = [
  { bg: "#f5d3a4", fg: "#6e3a13" }, // peach
  { bg: "#c9e7d8", fg: "#185c41" }, // mint
  { bg: "#cfdef9", fg: "#2d4d96" }, // sky
  { bg: "#ddd1f3", fg: "#4a3892" }, // lav
  { bg: "#f3cad2", fg: "#8b2c44" }, // pink
  { bg: "#ecdfb5", fg: "#6f5b1a" }, // cream
] as const;

function pickPastel(key: string) {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return PASTEL_PALETTE[hash % PASTEL_PALETTE.length];
}

export const Avatar = (props: {
  record?: Contact;
  width?: 20 | 25 | 40;
  height?: 20 | 25 | 40;
  title?: string;
}) => {
  const record = useRecordContext<Contact>(props);
  if (!record?.avatar && !record?.first_name && !record?.last_name) {
    return null;
  }

  const size = props.width || props.height;
  const sizeClass =
    props.width === 20
      ? `w-[20px] h-[20px]`
      : props.width === 25
        ? "w-[25px] h-[25px]"
        : "w-10 h-10";

  const initialsKey = initials(record);
  const pastel = pickPastel(initialsKey);

  return (
    <ShadcnAvatar className={sizeClass} title={props.title}>
      <AvatarImage src={record.avatar?.src ?? undefined} />
      <AvatarFallback
        className={size && size < 40 ? "text-[10px]" : "text-sm"}
        style={{ backgroundColor: pastel.bg, color: pastel.fg }}
      >
        {initialsKey}
      </AvatarFallback>
    </ShadcnAvatar>
  );
};
